<template>
  <v-app-bar :elevation="0" density="compact" class="standalone-topbar">
    <!-- ── Where am I ────────────────────────────────────────────────────
         The brand mark and app name live in the sidebar header; repeating
         them 12px to their right told the visitor nothing twice. This row
         answers the other question instead: which section is open. -->
    <div class="standalone-topbar__context">
      <v-icon v-if="contextIcon" size="17" class="standalone-topbar__context-icon">{{ contextIcon }}</v-icon>
      <span class="standalone-topbar__context-title">{{ contextTitle }}</span>
      <span v-if="contextParent" class="standalone-topbar__context-parent">{{ contextParent }}</span>
    </div>

    <v-spacer />

    <p v-if="switchError" class="standalone-topbar__error">{{ switchError }}</p>

    <v-menu v-if="showOrgSwitcher" location="bottom end">
      <template #activator="{ props }">
        <button type="button" class="standalone-topbar__chip standalone-topbar__org" v-bind="props">
          <LogoTile :src="currentOrgLogo" :alt="currentOrgName" :size="18" />
          <span class="standalone-topbar__chip-label">{{ currentOrgName }}</span>
          <v-icon size="14" class="standalone-topbar__chip-chev">unfold_more</v-icon>
        </button>
      </template>
      <div class="standalone-menu">
        <div class="standalone-menu__label">Switch organisation</div>
        <button
          v-for="org in session.accessibleOrgs"
          :key="org.id"
          type="button"
          class="sh-row"
          :class="{ 'sh-row--selected': org.id === session.currentOrg?.id }"
          :disabled="switching"
          @click="switchOrg(org)"
        >
          <LogoTile :src="org.logo || org.icon || null" :alt="org.name" :size="22" />
          <span class="sh-row__label">{{ org.name }}</span>
          <v-icon v-if="org.id === session.currentOrg?.id" size="16" color="primary">check</v-icon>
        </button>
      </div>
    </v-menu>
    <span v-else-if="session.currentOrg" class="standalone-topbar__chip standalone-topbar__org-static">
      <LogoTile :src="currentOrgLogo" :alt="currentOrgName" :size="18" />
      <span class="standalone-topbar__chip-label">{{ currentOrgName }}</span>
    </span>

    <v-btn
      icon
      variant="text"
      size="small"
      class="standalone-topbar__theme-toggle"
      :title="darkMode ? 'Switch to light mode' : 'Switch to dark mode'"
      @click="toggleTheme"
    >
      <v-icon size="19">{{ darkMode ? "light_mode" : "dark_mode" }}</v-icon>
    </v-btn>

    <!-- ── Who am I ──────────────────────────────────────────────────────
         Name beside the avatar, not just an initial: a bare monogram is
         indistinguishable from the "?" fallback the shell used to show for
         every silently-resumed session (see stores/session.ts). -->
    <v-menu location="bottom end">
      <template #activator="{ props }">
        <button type="button" class="standalone-topbar__user-btn" v-bind="props">
          <span class="standalone-topbar__avatar">
            <img v-if="userAvatar" :src="userAvatar" :alt="userFullName" referrerpolicy="no-referrer" />
            <span v-else>{{ userInitials }}</span>
          </span>
          <span v-if="userFullName" class="standalone-topbar__user-name">{{ userFirstName }}</span>
          <v-icon size="14" class="standalone-topbar__chip-chev">unfold_more</v-icon>
        </button>
      </template>
      <div class="standalone-menu">
        <div class="standalone-menu__user">
          <span class="standalone-topbar__avatar standalone-topbar__avatar--lg">
            <img v-if="userAvatar" :src="userAvatar" :alt="userFullName" referrerpolicy="no-referrer" />
            <span v-else>{{ userInitials }}</span>
          </span>
          <span class="standalone-menu__user-id">
            <span class="standalone-menu__user-name">{{ userFullName || "Signed in" }}</span>
            <span class="standalone-menu__user-email">{{ session.user?.emailAddress }}</span>
          </span>
        </div>
        <div class="standalone-menu__divider"></div>
        <button type="button" class="sh-row standalone-topbar__signout" @click="session.signOut()">
          <v-icon size="17" class="standalone-menu__row-icon">logout</v-icon>
          <span class="sh-row__label">Sign out</span>
        </button>
      </div>
    </v-menu>
  </v-app-bar>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, onUnmounted, ref } from "vue";
import { useTheme } from "vuetify";
import { useSessionStore } from "@/stores/session";
import LogoTile from "./LogoTile.vue";
import type { UserOrganisation } from "@/tenant/types";

export default defineComponent({
  name: "TopBar",
  components: { LogoTile },
  setup() {
    const session = useSessionStore();
    const theme = useTheme();
    const switching = ref(false);
    const switchError = ref<string | null>(null);
    const path = ref(window.location.pathname + window.location.search);

    function onRoutingEvent(): void {
      path.value = window.location.pathname + window.location.search;
    }
    onMounted(() => {
      window.addEventListener("popstate", onRoutingEvent);
      window.addEventListener("single-spa:before-routing-event", onRoutingEvent);
    });
    onUnmounted(() => {
      window.removeEventListener("popstate", onRoutingEvent);
      window.removeEventListener("single-spa:before-routing-event", onRoutingEvent);
    });

    const tenantName = computed(() => session.tenant?.name || "Metaship");

    /**
     * The nav entry (and sub-entry) the current URL sits in. Same matching
     * rule as Sidebar.isActive: a `?section=` route matches on path+section,
     * a bare route matches its path and anything nested under it.
     */
    const context = computed(() => {
      const [currentPath, currentQuery] = path.value.split("?");
      const currentSection = new URLSearchParams(currentQuery || "").get("section");
      for (const item of session.navItems) {
        const [itemPath] = item.route.split("?");
        if (currentPath !== itemPath && !(currentPath ?? "").startsWith(`${itemPath}/`)) continue;
        const sub = (item.items || []).find((s) => {
          const [subPath, subQuery] = s.route.split("?");
          return subPath === currentPath && new URLSearchParams(subQuery || "").get("section") === currentSection;
        });
        return sub
          ? { title: sub.name, parent: item.name, icon: sub.icon || item.icon || null }
          : { title: item.name, parent: null, icon: item.icon || null };
      }
      if (currentPath === "/") return { title: "Home", parent: null, icon: "home" };
      return { title: tenantName.value, parent: null, icon: null };
    });
    const contextTitle = computed(() => context.value.title);
    const contextParent = computed(() => context.value.parent);
    const contextIcon = computed(() => context.value.icon);

    const showOrgSwitcher = computed(
      () => session.tenant?.kind === "PRODUCT" && session.accessibleOrgs.length > 1,
    );
    const currentOrgName = computed(() => session.currentOrg?.name || "");
    const currentOrgLogo = computed(() => session.currentOrg?.logo || session.currentOrg?.icon || null);

    const darkMode = computed(() => theme.global.current.value.dark);
    function toggleTheme(): void {
      // The single source of truth for dark/light — flips body[data-theme]
      // too, which some hosted modules key off directly.
      window.MetaShipGlobalState?.toggleTheme();
      const isDark = window.MetaShipGlobalState?.getState().darkMode ?? true;
      theme.change(isDark ? "dark" : "light");
    }

    async function switchOrg(org: UserOrganisation): Promise<void> {
      if (org.id === session.currentOrg?.id || switching.value) return;
      switching.value = true;
      switchError.value = null;
      try {
        await session.selectOrganisation(org);
      } catch (e) {
        switchError.value = e instanceof Error ? e.message : "Could not switch organisation — please try again.";
      } finally {
        switching.value = false;
      }
    }

    const userFullName = computed(() => {
      const u = session.user;
      if (!u) return "";
      return `${u.firstname || ""} ${u.surname || ""}`.trim() || u.emailAddress || "";
    });
    const userFirstName = computed(() => session.user?.firstname || userFullName.value.split(" ")[0] || "");
    const userAvatar = computed(() => session.user?.avatar || null);
    const userInitials = computed(() => {
      const u = session.user;
      if (!u) return "?";
      const first = (u.firstname || u.emailAddress || "?").charAt(0);
      const second = (u.surname || "").charAt(0);
      return `${first}${second}`.toUpperCase();
    });

    return {
      session,
      contextTitle,
      contextParent,
      contextIcon,
      showOrgSwitcher,
      currentOrgName,
      currentOrgLogo,
      switching,
      switchError,
      switchOrg,
      darkMode,
      toggleTheme,
      userFullName,
      userFirstName,
      userAvatar,
      userInitials,
    };
  },
});
</script>

<style scoped>
.standalone-topbar {
  padding-left: var(--metaship-nav-width);
  transition: padding-left 150ms ease;
  background: var(--sh-chrome-bg) !important;
  /* An inset shadow, NOT `border-bottom`: a border adds a 49th pixel, and the
     bar's height is a published contract (`--metaship-header-height`, 48px)
     that every hosted module pads itself by. A 1px drift there puts the top of
     each module's content under the bar. */
  box-shadow: inset 0 -1px 0 var(--sh-hairline) !important;
}
.standalone-topbar :deep(.v-toolbar__content) {
  padding-right: 8px;
  gap: 6px;
}

.standalone-topbar__context {
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
  margin-left: 14px;
}
.standalone-topbar__context-icon {
  color: var(--sh-text-45) !important;
  align-self: center;
}
.standalone-topbar__context-title {
  font-size: 13.5px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--sh-text);
  white-space: nowrap;
}
.standalone-topbar__context-parent {
  font-size: 11px;
  color: var(--sh-text-45);
  white-space: nowrap;
}

/* Quiet pill for the org + user activators — same hover strength as a nav row. */
.standalone-topbar__chip,
.standalone-topbar__user-btn {
  display: flex;
  align-items: center;
  gap: 7px;
  height: 30px;
  padding: 0 8px;
  border: none;
  border-radius: 7px;
  background: transparent;
  color: var(--sh-text);
  font: inherit;
  cursor: pointer;
  transition: background 150ms ease;
}
.standalone-topbar__chip:hover,
.standalone-topbar__user-btn:hover {
  background: var(--sh-hover);
}
.standalone-topbar__org-static {
  cursor: default;
}
.standalone-topbar__org-static:hover {
  background: transparent;
}
.standalone-topbar__chip-label {
  font-size: 12px;
  font-weight: 600;
  max-width: 190px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.standalone-topbar__chip-chev {
  color: var(--sh-text-45) !important;
}

/* People are circles (brand marks are the white squares above). */
.standalone-topbar__avatar {
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  overflow: hidden;
  background: rgb(var(--v-theme-secondary));
  color: #fff;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.02em;
}
.standalone-topbar__avatar--lg {
  width: 38px;
  height: 38px;
  font-size: 14px;
}
.standalone-topbar__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.standalone-topbar__user-name {
  font-size: 12px;
  font-weight: 600;
  max-width: 140px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.standalone-topbar__error {
  color: rgb(var(--v-theme-error));
  font-size: 11px;
  margin: 0 8px;
}

@media (max-width: 700px) {
  .standalone-topbar__chip-label,
  .standalone-topbar__user-name,
  .standalone-topbar__context-parent {
    display: none;
  }
}
</style>

<!-- Menus teleport to <body>, outside this component's scope — these two
     surfaces are styled globally instead. -->
<style>
.standalone-menu {
  min-width: 230px;
  padding: 6px;
  border-radius: 12px;
  background: rgb(var(--v-theme-card));
  border: 1px solid var(--sh-hairline);
  box-shadow: 0 18px 40px -20px rgba(0, 0, 0, 0.6);
}
.standalone-menu__label {
  padding: 5px 8px 6px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--sh-text-45);
}
.standalone-menu__user {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px;
}
.standalone-menu__user-id {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.3;
}
.standalone-menu__user-name {
  font-size: 13px;
  font-weight: 700;
  color: var(--sh-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.standalone-menu__user-email {
  font-size: 11px;
  color: var(--sh-text-45);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.standalone-menu__divider {
  height: 1px;
  margin: 5px 0;
  background: var(--sh-hairline);
}
.standalone-menu__row-icon {
  color: var(--sh-text-60) !important;
}
.standalone-menu .sh-row {
  height: 36px;
}
.standalone-menu .sh-row--selected {
  background: var(--sh-active);
}
</style>
